import React, { useCallback } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { SingleField } from '../fields/SingleField';
import { getOpenFieldsAndSections, toggleOpenFieldSection } from '../store';
import { CollateralTakerInsolvency, UpdatedOpinionField } from '../store/types';
import styles from './OpinionSection.module.scss';
import { SectionWrapper } from './SectionWrapper';

interface CollateralTakerInsolvencyProps {
    collateralTakerInsolvency: CollateralTakerInsolvency;
    previousCollateralTakerInsolvency: CollateralTakerInsolvency | null;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedOpinionField[];
}

export const CollateralTakerInsolvencySection: React.FC<CollateralTakerInsolvencyProps> = ({ collateralTakerInsolvency, previousCollateralTakerInsolvency, isEditing, isUpdating, fieldsUpdated }) => {
    const dispatch = useAppDispatch();
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const getFieldOpen = useCallback((id: string) => openFieldsAndSections.includes(id), [openFieldsAndSections]);
    const toggleFieldOpen = useCallback((id: string) => dispatch(toggleOpenFieldSection(id)), [dispatch]);
    const sectionId = 'collateralTakerInsolvency';
    const { providerRightsEnforceable, providerRequirements, allOutstandingSatisfied, subjectToStayOrFreeze, cashLeakageConclusion } = collateralTakerInsolvency;

    const getPreviousField = useCallback((fieldId: keyof CollateralTakerInsolvency) => !isNull(previousCollateralTakerInsolvency) && previousCollateralTakerInsolvency[fieldId] || null, [previousCollateralTakerInsolvency]);

    return (
        <SectionWrapper id={sectionId} label='Collateral Taker Insolvency' isEditing={isEditing} isUpdating={isUpdating}>
            <div className={styles.collateralTakerInsolvencySectionWrapper}>
                <div className={styles.sectionHeaderLabel}>Under the laws of the jurisdiction under consideration:</div>
                <SingleField
                    field={providerRightsEnforceable}
                    previousField={getPreviousField('providerRightsEnforceable')}
                    fieldId='providerRightsEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={providerRequirements}
                    previousField={getPreviousField('providerRequirements')}
                    fieldId='providerRequirements'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={allOutstandingSatisfied}
                    previousField={getPreviousField('allOutstandingSatisfied')}
                    fieldId='allOutstandingSatisfied'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={subjectToStayOrFreeze}
                    previousField={getPreviousField('subjectToStayOrFreeze')}
                    fieldId='subjectToStayOrFreeze'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={cashLeakageConclusion}
                    previousField={getPreviousField('cashLeakageConclusion')}
                    fieldId='cashLeakageConclusion'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
            </div>
        </SectionWrapper>
    );
};
